<template>
    <v-row no-gutters class="fill-height">
      <v-col class="px-6 pt-2 pb-12 fill-height">
        <h1 class="display-1">
          {{ $t("installations") }}
          <span>
            {{ installationsLength }} {{ $t("total") }}
          <v-btn
            icon
            small
            color="textColor"
            class="refresh"
            @click="refreshData()"
            >
            <v-icon small>mdi-autorenew</v-icon>
          </v-btn>
          </span>
        </h1>
        <h2 class="subtitle-2">{{ $t("installationsSubtitle") }} </h2>
        <div class="pt-5 pb-5 d-flex align-end justify-space-between">
          <v-col class="pb-0" cols="5">
            <v-text-field v-model="model.search" append-icon="mdi-magnify" :label="searchLabel" @input="onSearchChange"  single-line clearable
              hide-details />
          </v-col>
          <div class="pb-0" cols="4">
            <v-btn         
              color="primary"
              class="#e4e4e9--text" 
              @click="addInstallation" rounded
              >
              
              {{ $t("installationAdd") }}
            </v-btn>
          </div>
        </div>
        <v-sheet>
          <v-data-table :headers="tableheaders" :items="installations" v-model="model.selected" :footer-props="{ 'items-per-page-options': [5, 10, 15, 50] }"
            fixed-header max-height="450px" item-key="tagId" class="installation-table max-height elevation-1"
            @click:row="selectInstallation" :loading="isLoadingTable" :loading-text="$t('loadingData')">
            <template v-slot:[`item.balance`]="{ item }">
              <div v-if="item.balance > 0">
                <div style="color:red">{{formatBalance(item.balance)}}<v-icon x-small color="error">
                  mdi-currency-eur
                </v-icon></div>
              </div>
              <div v-if="item.balance == 0">
                <div style="color:green">{{formatBalance(item.balance)}}<v-icon x-small color="success">
                  mdi-currency-eur
                </v-icon></div>
              </div>
            </template>
            <template v-slot:[`item.is_premium`]="{ item }">
              <v-icon v-if="item.is_premium" color="success">
                mdi-star-box-outline
              </v-icon>
  
            </template>
            <template v-slot:[`item.client_name`]="{item}">
              <div class="d-flex">
                <v-tooltip top :disabled="!item.client_is_archived">
                  <template v-slot:activator="{ on }">
                    <div v-on="on">
                      {{ item.is_business ? `${item.entity_name} (${item.client_name})` : item.client_name }} <v-icon color="#ff9e39" v-if="item.client_is_archived"> mdi-alert-circle </v-icon>
                    </div>
                  </template>
                  <span style="color:black">{{ $t("client_has_been_deleted") }}</span>
                </v-tooltip>
              </div>
            </template>
            <template v-slot:[`item.address`]="{ item }">
              {{ item.address && item.address.length > 0 ? item.address : '-' }}
            </template>
            <template v-slot:[`item.notes`]="{ item }">
              <v-tooltip top v-if="item.notes && item.notes.length >= 18">
                <template v-slot:activator="{ on }">
                  <div class="text-truncate" style="max-width: 130px;" v-on="on">{{ item.notes }}</div>
                </template>
                <span class="spanWordWrapNotes" style="color:black">{{ item.notes }}</span>
              </v-tooltip>
              <div v-else>{{ item.notes && item.notes.length > 0 ? item.notes : '-' }}</div>
            </template>
            <template v-slot:[`item.description`]="{ item }">
              <v-tooltip top v-if="item.description && item.description.length >= 18">
                <template v-slot:activator="{ on }">
                  <div class="text-truncate" style="max-width: 130px;" v-on="on">{{ item.description }}</div>
                </template>
                <span class="spanWordWrap" style="color:black">{{ item.description }}</span>
              </v-tooltip>
              <div v-else>{{ item.description && item.description.length > 0 ? item.description : '-' }}</div>
            </template>
            <template v-slot:no-data>
              {{ $t("installationsEmpty") }}
            </template>
            <!-- does not use deviceInfo, but it needs to be attached to a value in devices, loggedIn is already used -->
            <template v-slot:[`item.id`]="{ item }">
              <Dropdown>
                <DropdownItem title="view" @clicked="selectInstallation(item)" />
                <DropdownItem title="edit" @clicked="editInstallation(item)" />
                <DropdownItem title="delete" @clicked="deleteInstallation(item)" />
              </Dropdown>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>
  </template>
  
  <script lang="ts">
  import { Component, Vue, Watch } from 'vue-property-decorator'
  import { commonModule, installationsModule, filterModule, companyModule } from '@/store/modules/store-accessor'
  import Dropdown from '../components/dropdown/Dropdown.vue'
  import DropdownItem from '../components/dropdown/DropdownItem.vue'
  import FilterDropdown from '../components/dropdown/filter/FilterDropdown.vue'
  import { convertEnglishToGreek, sortTableDates } from "@/helpers/data";
  
  @Component({
    components: { Dropdown, DropdownItem, FilterDropdown }
  })
  export default class Installations extends Vue {
    tableheaders: any [] = [
      {
        text: '',
        align: 'left',
        class: 'row-style',
        sortable: false,
        value: 'is_premium',
        width: '10px'
      },
      {
        text: this.$t("tag"),
        align: 'left',
        class: 'row-style',
        sortable: true,
        value: 'tag_split'
      },
      { text: this.$t("client_name"), sortable: true, class: 'row-style', value: 'client_name' },
      { text: this.$t("address"), sortable: true, class: 'row-style', value: 'address' },
      { text: this.$t("area"), sortable: true, class: 'row-style', value: 'area' },
      { text: this.$t("notes"), sortable: true, class: 'row-style', value: 'notes' },
      { text: '', align: 'right', sortable: false, class: 'row-style', value: 'id' } 
    ]
  
    model: any = {
      search: '',
      selected: [] as string[]
    }
  
    timer: number;
  
    onSearchChange(val: string) {
      clearTimeout(this.timer)
  
      this.timer = window.setTimeout(async () => {
        installationsModule.setIsLoadingTable(true);
        if (val && val.length > 0) {
          val = convertEnglishToGreek(val);
          installationsModule.setSearch(val);
          installationsModule.queryObject.search = installationsModule.search;
        }
        else {
          installationsModule.setSearch('');
          delete installationsModule.queryObject.search;
        }
      this.$router.replace({ query: installationsModule.queryObject });
  
       // Check if user selected any of the queries
       const queryIndex = this.$route.fullPath.indexOf('?');
      if(queryIndex >= 0) {
        const query = this.$route.fullPath.substring(queryIndex);
        installationsModule.setFilters(query);
      } else {
        installationsModule.setFilters("");
      }
  
      await installationsModule.getInstallationsAction(installationsModule.filters);
      installationsModule.setIsLoadingTable(false);
      }, 500);
    }
  
    get installationsLength() {
      return installationsModule.installations.length
    }
  
    get searchLabel() {
      return this.$t("installationsSearch")
    }
  
    get installations(){
      return installationsModule.installations
    }
  
    get currInstallation(){
      return installationsModule.currentInstallation
    }
  
    get isLoadingTable() {
      return installationsModule.isLoadingTable;
    }
  
    async mounted () {
      /*
       TODO: Handle installations properly when UX is clear.
       */
      installationsModule.getInstallationsCount();
      installationsModule.getInstallationsAction('');
  
      // Clear the search input and all of the other filters when visiting the page
      installationsModule.setSearch('');
      installationsModule.setFilters('');
      installationsModule.setQueryObject({});
  
      commonModule.getPaymentTypeCombo();
      await companyModule.getCompanyInfo();
      commonModule.initSnackbar({});
  
      if((companyModule.company?.preferences as any)?.installation_description) {
        this.tableheaders.splice(2, 0, { text: this.$t("description"), sortable: true, class: 'row-style', value: 'description' });
      }
    }
  
    destroyed() {
      installationsModule.clearInstallations();
    }
  
    async selectInstallation (installation: any) {
      try {
        installationsModule.setIsLoadingTable(true);
        await installationsModule.getInstallationInfo(installation.id);
        installationsModule.setIsLoadingTable(false);
        commonModule.showSideview({ name: 'installations-sideview', payload: { installationId: installation.id } })
      } catch (error) {
        installationsModule.setIsLoadingTable(false);
      }
    }
  
    async addInstallation (){
      await installationsModule.getInstallationNextTagId();
      commonModule.showModal({ name: 'add-installation-modal' })
    }
  
    deleteInstallation(installation: any) {
      commonModule.showModal({ name: 'delete-installation-modal', payload: { installation: installation}})
    }
  
    async editInstallation (installation: any) {
      await installationsModule.getInstallationInfo(installation.id);
      commonModule.showModal({ name: 'edit-installation-modal', payload: { view: "installations"} })
    }
  
    async refreshData() {
      await installationsModule.getInstallationsCount();
      await installationsModule.getInstallationsAction(installationsModule.filters);
    }
  
    get descriptionIsEnabled() {
      return (companyModule.company?.preferences as any)?.installation_description;
    }
  
  }
  </script>
  
  <style scoped lang="scss">
  .installation-table:not(thead) {
    cursor: pointer;
    min-width: 600px;
    border: 1px solid rgba(0, 0, 0, 0.12);
  }
  
  
  .max-height {
    max-height: 65vh;
    overflow: auto;
  }
  
  .placeholder {
    background-color: grey
  }
  
  h1 {
    margin-top: 40px;
    font-size: 35px;
    font-weight: 500;
    line-height: 65px;
  }
  
  span {
    margin-left: 10px;
    font-size: 18px;
    font-weight: 400;
    color: $pale-purple;
  }
  
  .v-tooltip__content {
    background-color: white;
    color: black;
    font-size: 14px;
    font-family: Arial, sans-serif;
    border-style: solid ;
    border-color: #777777;
    border-width: 1px;
  }
  
  .spanWordWrap {
      width: 200px;
      word-wrap: break-word;
  }
  
  .spanWordWrapNotes {
      display: block;
      width: 200px;
      word-wrap: break-word;
  }
  
  .refresh {
    margin-bottom: 3px;
  }
  </style>
  